import { DecimalPipe } from '@angular/common'
import { Component, computed, effect, inject, signal } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { ChartComponent } from '@components/chart/chart.component'
import { PROJECT_OM4A } from '@core/constants/constant.data'
import { LAYERS_ARRAY } from '@core/constants/layers.data'
import { LayerInResults } from '@core/models/result-manager.model'
import { CalculationModuleService } from '@core/services/calculation-module.service'
import { CMSessionService } from '@core/services/cm-session.service'
import { LayerResultService } from '@core/services/layer-result.service'
import { environment } from '@env/environment'
import { BusinessNamePipe } from '@shared/pipes/businessNameIndicators.pipe'
import { LayerNamePipe } from '@shared/pipes/businessNameLayers.pipe'
import { NumberFormatPipe } from '@shared/pipes/number-format.pipe'
import { ExportDataComponent } from './export-data/export-data.component'
import { CM_CATEGORY, DROP_DOWN_RESULT_LIST } from './result-manager.data'

@Component({
  selector: 'app-result-manager',
  templateUrl: 'result-manager.component.html',
  styleUrls: ['result-manager.component.scss'],
  imports: [
    //Forms
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatSelectModule,

    // Components
    ChartComponent,
    ExportDataComponent,

    // Pipes
    LayerNamePipe,
    BusinessNamePipe,
    NumberFormatPipe,
    DecimalPipe,
  ],
})
export class ResultManagerComponent {
  private readonly _layerResultService = inject(LayerResultService)
  cmSessionService = inject(CMSessionService)
  calculationModuleService = inject(CalculationModuleService)

  isCMRunning$ = this.calculationModuleService.isCMRunning$
  isLayerResultsLoading$ = this._layerResultService.isLayerResultsLoading$

  // Used in template
  activeCMSessions$ = this.cmSessionService.activeCMSessions$
  allLayerResults$ = this._layerResultService.allLayerResults$
  uploadedLayerResults$ = this._layerResultService.uploadedLayerResults$

  allResultsWithCategory$ = computed(() => {
    const layerResults = this.allLayerResults$()

    // Attach category to each layer
    const layersWithCategory = layerResults.layers.map((layer) => ({
      ...layer,
      category: this.getCategory(layer),
    }))

    return {
      layers: layersWithCategory,
    }
  })

  // Used in template
  filteredLayers$ = computed<LayerInResults[]>(() => {
    const selectedRef = this.selectedCategoryRef$()
    const { layers = [] } = this.allResultsWithCategory$()
    if (selectedRef === 'overall') return layers
    return layers.filter((layer) => layer.category === selectedRef)
  })

  dropDownCategories$ = computed<{ name: string; ref: string }[]>(() => {
    const { layers = [] } = this.allResultsWithCategory$()
    const uniqueCategories = new Set<string>()

    layers.forEach((layer) => {
      if (layer.category) uniqueCategories.add(layer.category)
    })

    const result = Array.from(uniqueCategories)
      .filter(Boolean)
      .map((category) => ({ name: category, ref: category }))

    if (this.activeCMSessions$()) {
      if (this.activeCMSessions$().length > 0) {
        return [DROP_DOWN_RESULT_LIST[0], CM_CATEGORY, ...result]
      }
    }

    return [DROP_DOWN_RESULT_LIST[0], ...result]
  })

  selectedCategoryRef$ = signal(DROP_DOWN_RESULT_LIST[0].ref)

  tabIndicators = 'indicators'
  tabCharts = 'charts'
  tabSelected$ = signal<string>(this.tabIndicators)
  appName = environment.appName
  PROJECT_OM4A = PROJECT_OM4A

  constructor() {
    // To reset dropdown filter if dropdown disappears
    effect(() => {
      if (this.dropDownCategories$().length <= 2) {
        this.onCategoryChange(DROP_DOWN_RESULT_LIST[0].ref)
      }
    })
  }

  tabSwitch(tabName: string) {
    this.tabSelected$.set(tabName)
  }

  onCategoryChange(ref: string) {
    this.selectedCategoryRef$.set(ref)
  }

  getCategory = (layer: LayerInResults) => {
    // personal and shared layers
    if (layer.category) {
      return layer.category
    }
    // normal layer
    const layerInfo = LAYERS_ARRAY.find((l) => l.workspacename === layer.name)
    return layerInfo?.category || null
  }
}
